/** QuotePublic group (canonical `qWXPyzD2`, spec §7/§8). Unauthenticated. */

import { api } from "./client";
import { endpoint } from "@/config/env";
import { normaliseLineItems } from "@/lib/lineItems";
import type {
  PublicReadResponse,
  TrackBody,
  AcceptBody,
  AcceptResponse,
  DeclineBody,
  EngagementSessionBody,
} from "@/types";

/** 180 GET quote/by-slug/{slug} — pure read, no mutation (bot-safe).
 *  Returns a `state`-discriminated response (spec §8). */
export function readBySlug(slug: string, signal?: AbortSignal): Promise<PublicReadResponse> {
  return api
    .get<PublicReadResponse>("public", `quote/by-slug/${encodeURIComponent(slug)}`, {
      auth: false,
      signal,
    })
    .then(normaliseLineItems);
}

/** 181 POST quote/by-slug/{slug}/track — fire AFTER mount (spec §8) to avoid
 *  false views from link-preview bots. */
export function track(slug: string, body: TrackBody): Promise<unknown> {
  return api.post<unknown>("public", `quote/by-slug/${encodeURIComponent(slug)}/track`, {
    auth: false,
    body,
  });
}

/** 182 POST quote/by-slug/{slug}/accept — idempotent; locks accepted. Body
 *  carries the client's selected optional line-item ids; server recomputes and
 *  snapshots the authoritative `accepted_total`. */
export function accept(slug: string, body: AcceptBody): Promise<AcceptResponse> {
  return api.post<AcceptResponse>("public", `quote/by-slug/${encodeURIComponent(slug)}/accept`, {
    auth: false,
    body,
  });
}

/** 183 POST quote/by-slug/{slug}/decline — idempotent; locks declined. */
export function decline(slug: string, body: DeclineBody): Promise<PublicReadResponse> {
  return api.post<PublicReadResponse>("public", `quote/by-slug/${encodeURIComponent(slug)}/decline`, {
    auth: false,
    body,
  });
}

/** 333 POST quote/by-slug/{slug}/session — mint the beacon's per-page-load session row.
 *  Fire-and-forget alongside track; idempotent on session_uid, and it deliberately does not
 *  touch view_count (181 owns the quote-row aggregates). */
export function engagementSession(slug: string, body: EngagementSessionBody): Promise<unknown> {
  return api.post<unknown>("public", `quote/by-slug/${encodeURIComponent(slug)}/session`, {
    auth: false,
    body,
  });
}

/** 334 POST quote/by-slug/{slug}/events — the shared engagement beacon POSTs here ITSELF
 *  (fetch keepalive / sendBeacon, outside the api client, so the final flush survives the page
 *  going away), which is why this exposes the URL rather than a call. */
export function engagementEventsUrl(slug: string): string {
  return endpoint("public", `quote/by-slug/${encodeURIComponent(slug)}/events`);
}
